// packages/shared/src/store/useTimerStore.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  deriveElapsedMs,
  type PauseSegment,
  type TimerStatus,
} from '../services/timeTracking/timerMath';

export interface TimerStopPayload {
  startedAt: string;
  durationMinutes: number;
  dossierId: string;
  taskId: string | null;
  pauses: PauseSegment[];
}

interface TimerStore {
  status: TimerStatus;
  dossierId: string | null;
  taskId: string | null;
  startedAt: string | null;
  pauses: PauseSegment[];
  start: (dossierId: string, taskId?: string | null) => void;
  pause: () => void;
  resume: () => void;
  /** Computes payload (rounded minutes) and resets to idle. Returns null if not running/paused. */
  stop: () => TimerStopPayload | null;
  /** Re-enter running state from a stop payload, preserving exact startedAt + pauses. */
  restore: (payload: TimerStopPayload) => void;
  reset: () => void;
}

const IDLE = {
  status: 'idle' as TimerStatus,
  dossierId: null,
  taskId: null as string | null,
  startedAt: null as string | null,
  pauses: [] as PauseSegment[],
};

export const useTimerStore = create<TimerStore>()(
  persist(
    (set, get) => ({
      ...IDLE,
      start: (dossierId, taskId = null) => {
        if (get().status !== 'idle') return; // single timer
        set({
          status: 'running',
          dossierId,
          taskId,
          startedAt: new Date().toISOString(),
          pauses: [],
        });
      },
      pause: () => {
        if (get().status !== 'running') return;
        set((s) => ({
          status: 'paused',
          pauses: [...s.pauses, { pausedAt: new Date().toISOString(), resumedAt: null }],
        }));
      },
      resume: () => {
        if (get().status !== 'paused') return;
        set((s) => {
          const lastIdx = s.pauses.length - 1;
          const last = s.pauses[lastIdx];
          const pauses =
            last && last.resumedAt === null
              ? [
                  ...s.pauses.slice(0, lastIdx),
                  { ...last, resumedAt: new Date().toISOString() },
                ]
              : s.pauses;
          return { status: 'running', pauses };
        });
      },
      stop: () => {
        const s = get();
        if (s.status === 'idle' || !s.startedAt || !s.dossierId) return null;
        const elapsedMs = deriveElapsedMs(
          { status: s.status, startedAt: s.startedAt, pauses: s.pauses },
          Date.now(),
        );
        const closedPauses = s.pauses.map((seg, i) =>
          i === s.pauses.length - 1 && seg.resumedAt === null
            ? { ...seg, resumedAt: new Date().toISOString() }
            : seg,
        );
        const payload: TimerStopPayload = {
          startedAt: s.startedAt,
          durationMinutes: Math.max(1, Math.round(elapsedMs / 60000)),
          dossierId: s.dossierId,
          taskId: s.taskId,
          pauses: closedPauses,
        };
        set({ ...IDLE });
        return payload;
      },
      restore: (payload) =>
        set({
          status: 'running',
          dossierId: payload.dossierId,
          taskId: payload.taskId,
          startedAt: payload.startedAt,
          pauses: payload.pauses,
        }),
      reset: () => set({ ...IDLE }),
    }),
    {
      name: 'cime.timer.active',
      partialize: (s) => ({
        status: s.status,
        dossierId: s.dossierId,
        taskId: s.taskId,
        startedAt: s.startedAt,
        pauses: s.pauses,
      }),
    },
  ),
);
